'use client';

import { useCallback, useState } from 'react';
import axios from 'axios';
import { apiPaths } from '@/constants/apiPaths';

/** Keeps a slow backend from stalling a form step indefinitely. */
const MEMBERSHIP_LOOKUP_TIMEOUT_MS = 5000;

export interface CustomerStatus {
  isCustomer: boolean;
  isClubMember: boolean;
}

/**
 * Looks up whether an email address belongs to a customer and, if so, to an active Nuki Club
 * member. Mirrors the lookup the Club registration tile performs on its email step.
 *
 * The lookup rethrows on failure so the caller can decide how to degrade — it must never
 * silently report "not a member" for what was actually a network problem.
 */
export function useClubMembershipLookup() {
  const [isChecking, setIsChecking] = useState(false);

  const checkMembership = useCallback(async (email: string): Promise<CustomerStatus> => {
    setIsChecking(true);

    try {
      const { data } = await axios.get(
        apiPaths.customers.isCustomer + encodeURIComponent(email.trim()),
        { timeout: MEMBERSHIP_LOOKUP_TIMEOUT_MS },
      );

      return {
        isCustomer: Boolean(data?.isCustomer),
        isClubMember: Boolean(data?.isClubMember),
      };
    } catch (error) {
      console.error('Club membership lookup failed', error);
      throw error;
    } finally {
      setIsChecking(false);
    }
  }, []);

  return { checkMembership, isChecking };
}
